import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Button, Jumbotron, Row, Spinner, Col,
} from 'reactstrap';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { GET_FIRMWARE_RELEASES } from '../../query/query';
import {
  CREATE_FIRMWARE_RELEASE, SET_FIRMWARE_RELEASE_LIMIT, UPDATE_FIRMWARE_RELEASE_STATUS,
  DELETE_FIRMWARE_RELEASE, SET_FIRMWARE_RELEASE_ACTIVE,
} from '../../mutation/mutation';
import { StatsRender } from './Stats';

const createFirmwareReleaseMutation = () => useMutation(CREATE_FIRMWARE_RELEASE, {
  update(cache, { data: { savCreateFirmwareRelease: release } }) {
    if (!release) { return; }
    const { savGetFirmwareReleases } = cache.readQuery({ query: GET_FIRMWARE_RELEASES });
    savGetFirmwareReleases.push(release);
    cache.writeQuery({ query: GET_FIRMWARE_RELEASES, data: { savGetFirmwareReleases } });
  },
});

const updateFirmwareReleaseStatusMutation = () => useMutation(UPDATE_FIRMWARE_RELEASE_STATUS, {
  update(cache, { data: { savUpdateFirmwareReleaseStatus: release } }) {
    if (!release) { return; }
    const { savGetFirmwareReleases } = cache.readQuery({ query: GET_FIRMWARE_RELEASES });
    const releases = savGetFirmwareReleases.filter((r) => r.version !== release.version);
    releases.unshift(release);
    cache.writeQuery({ query: GET_FIRMWARE_RELEASES, data: { savGetFirmwareReleases: releases } });
  },
});

const setFirmwareReleaseLimitMutation = () => useMutation(SET_FIRMWARE_RELEASE_LIMIT, {
  update(cache, { data: { savSetFirmwareReleaseLimit: release } }) {
    if (!release) { return; }
    const { savGetFirmwareReleases } = cache.readQuery({ query: GET_FIRMWARE_RELEASES });
    const releases = savGetFirmwareReleases.filter((r) => r.version !== release.version);
    releases.unshift(release);
    cache.writeQuery({ query: GET_FIRMWARE_RELEASES, data: { savGetFirmwareReleases: releases } });
  },
});

const deleteReleaseMutation = () => useMutation(DELETE_FIRMWARE_RELEASE, {
  update(cache, { data: { savDeleteFirmwareRelease: release } }) {
    if (!release) { return; }
    const { savGetFirmwareReleases } = cache.readQuery({ query: GET_FIRMWARE_RELEASES });
    const firmwareReleases = savGetFirmwareReleases.filter(({ version }) => version !== release.version);
    cache.writeQuery({ query: GET_FIRMWARE_RELEASES, data: { savGetFirmwareReleases: firmwareReleases } });
  },
});

const setFirmwareReleaseActiveMutation = () => useMutation(SET_FIRMWARE_RELEASE_ACTIVE, {
  update(cache, { data: { savSetFirmwareReleaseActive: release } }) {
    if (!release) { return; }
    const { savGetFirmwareReleases } = cache.readQuery({ query: GET_FIRMWARE_RELEASES });
    const firmwareReleases = savGetFirmwareReleases.map((r) => ({ ...r, active: r.version === release.version }));
    cache.writeQuery({ query: GET_FIRMWARE_RELEASES, data: { savGetFirmwareReleases: firmwareReleases } });
  },
});

const confirmDeleteRelease = ({ deleteRelease, release: { version } }) => {
  // eslint-disable-next-line no-restricted-globals
  if (confirm(`Delete release ${version}`)) {
    return deleteRelease({ variables: { version } });
  }
  return new Promise((resolve, reject) => reject());
};

const confirmFirmwareReleaseActive = ({ setFirmwareReleaseActive, release: { version } }) => {
  // eslint-disable-next-line no-restricted-globals
  if (confirm(`Start release of ${version}`)) {
    return setFirmwareReleaseActive({ variables: { version } });
  }
  return new Promise((resolve, reject) => reject());
};

const setReleaseLimit = ({ release, limit, setRelease }) => {
  setRelease({ ...release, limit });
};

export default () => {
  const [version, setVersion] = useState(null);
  const [release, setRelease] = useState(null);
  const [limit, setLimit] = useState(0);
  const {
    data: { savGetFirmwareReleases: releases = [] } = {},
    loading,
  } = useQuery(GET_FIRMWARE_RELEASES) || {};
  const [createFirmwareRelease] = createFirmwareReleaseMutation();
  const [updateFirmwareReleaseStatus, { loading: statusLoading }] = updateFirmwareReleaseStatusMutation();
  const [setFirmwareReleaseLimit] = setFirmwareReleaseLimitMutation();
  const [deleteRelease] = deleteReleaseMutation();
  const [setFirmwareReleaseActive, { loading: activeLoading }] = setFirmwareReleaseActiveMutation();
  const updateAll = useCallback(async () => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Release to all boxes`)) {
      await setFirmwareReleaseLimit({ variables: { version: release.version, limit: -1 } });
      setReleaseLimit({ release, limit: -1, setRelease });
    }
  }, [version, release, setLimit]);

  useEffect(() => {
    if (!release) {
      setRelease(releases.find(({ active }) => active));
    }
  }, [releases]);

  useEffect(() => {
    if (release) {
      setLimit(release.limit);
    }
  }, [release]);

  return (
    <Jumbotron>
      <Col>
        <h1>
          Production release
          <span aria-label="rocket" role="img">🚀</span>
        </h1>
        <Row>
          <input
            placeholder="Version number"
            type="text"
            onChange={({ target: { value } }) => setVersion(value)}
            value={version}
          />
          <Button
            color="success"
            onClick={() => createFirmwareRelease({ variables: { version } })}
            style={{ marginLeft: 30 }}
          >
            Create new release
          </Button>
        </Row>
        {
          (loading || !releases.length)
            ? <Spinner color="primary" style={{ marginLeft: 50 }} />
            : (
              <Row style={{ marginTop: 30 }}>
                <Col>
                  {releases.map((r) => (
                    <Row
                      onClick={() => { setRelease(r); }}
                      style={{
                        borderBottom: '1px grey solid', justifyContent: 'space-between', padding: 5,
                        backgroundColor: (r.version === (release || {}).version) ? 'lightgrey' : 'white',
                        cursor: 'pointer', alignItems: 'center',
                      }}
                    >
                      <div>
                        <span key={r.version}>
                          {r.version}
                        </span>
                        {r.active && (
                          <span>
                            <Badge color="primary" style={{ marginLeft: 10 }}>Active</Badge>
                            {(r.limit > 0 || r.limit === -1) && (
                              <span style={{ marginLeft: 10, color: 'grey', fontWeight: 400 }}>
                                {`${r.updated} / ${r.limit}`}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      <Button
                        color="danger"
                        onClick={() => confirmDeleteRelease({ release: r, deleteRelease })}
                      >
                        X
                      </Button>
                    </Row>
                  ))}
                </Col>
              </Row>
            )
        }
        {
          (release) && (
            <Row style={{ marginTop: 30 }}>
              <Col>
                {(release.active)
                  ? (
                    <Row style={{ marginTop: 30 }}>
                      <Col>
                        <StatsRender
                          succeed={release.status.succeed}
                          inProgress={release.status.inProgress}
                          failed={release.status.failed}
                          failedButConnected={release.status.failedButConnected}
                          failedTooManyAttempts={
                            release.status.failedTooManyAttempts
                          }
                          total={release.updated}
                        />
                        {
                          ((release.status.failedMacAddresses || []).length > 0) && (
                            <Row>
                              <span style={{ fontWeight: 'bold' }}>Failures:&nbsp;</span>
                              <span>{`${release.status.failedMacAddresses.join(', ')}`}</span>
                            </Row>
                          )
                        }
                        {
                          ((release.status.failedButConnectedMacAddresses || []).length > 0) && (
                            <Row>
                              <span style={{ fontWeight: 'bold' }}>Failures (but connected, will try again every hour):&nbsp;</span>
                              <span>{`${release.status.failedButConnectedMacAddresses.join(', ')}`}</span>
                            </Row>
                          )
                        }
                        {
                          ((release.status.failedTooManyAttemptsMacAddresses || []).length > 0) && (
                            <Row>
                              <span style={{ fontWeight: 'bold' }}>Failures (aborted, too many attempts):&nbsp;</span>
                              <span>{`${release.status.failedTooManyAttemptsMacAddresses.join(', ')}`}</span>
                            </Row>
                          )
                        }
                        <Row style={{ marginTop: 30 }}>
                          {(statusLoading)
                            ? <Spinner color="primary" style={{ marginLeft: 50 }} />
                            : (
                              <Button onClick={() => updateFirmwareReleaseStatus({
                                variables: { version: release.version },
                              })}
                              >
                                Refresh status
                              </Button>
                            )}
                        </Row>
                        <Row style={{ marginTop: 30 }}>
                          <h3 style={{ marginRight: 30 }}>{`Current limit: ${release.limit}`}</h3>
                          <input
                            placeholder={release.limit}
                            type="number"
                            onChange={({ target: { value } }) => setLimit(+value)}
                            value={limit}
                          />
                          <Button
                            color="success"
                            onClick={() => setFirmwareReleaseLimit({
                              variables: { version: release.version, limit },
                            })
                              .then(() => setReleaseLimit({ release, limit, setRelease }))}
                            style={{ marginLeft: 50 }}
                          >
                            Set limit
                          </Button>
                          <Button
                            color="danger"
                            onClick={updateAll}
                            style={{ marginLeft: 50 }}
                          >
                            Release to all boxes
                          </Button>
                        </Row>
                      </Col>
                    </Row>
                  )
                  : (
                    <Row style={{ marginTop: 30 }}>
                      {
                        activeLoading
                          ? <Spinner color="primary" style={{ marginLeft: 50 }} />
                          : (
                            <Button
                              color="primary"
                              onClick={() => confirmFirmwareReleaseActive({ setFirmwareReleaseActive, release })
                                .then(({ data: { savSetFirmwareReleaseActive } }) => setRelease({ ...release, ...savSetFirmwareReleaseActive }))
                                .catch(() => null)}
                            >
                              Set active release
                            </Button>
                          )
                      }
                    </Row>
                  )}
              </Col>
            </Row>
          )
        }
      </Col>
    </Jumbotron>
  );
};



// WEBPACK FOOTER //
// ./src/components/ota/ProductionReleasePanel.js